<script lang="ts" setup>
import VueSlider from 'vue-3-slider-component';
import { debounce } from '@antfu/utils';
import type { RangeFilter } from '~/types/Catalog';

const { filter } = defineProps<{ filter: RangeFilter }>();

const range = ref([Number(filter.details.limit_min), filter.details.limit_max]);
const minValue = ref(String(filter.details.values[0]));
const maxValue = ref(String(filter.details.values[1]));

watch(
  () => filter.details.values,
  () => {
    minValue.value = String(filter.details.values[0]);
    maxValue.value = String(filter.details.values[1]);

    range.value = [
      Number(minValue.value === '0' ? filter.details.limit_min : minValue.value),
      Number(maxValue.value === '0' ? filter.details.limit_max : maxValue.value),
    ];
  },
  { immediate: true },
);

watch(range, () => {
  minValue.value = String(range.value[0]);
  maxValue.value = String(range.value[1]);
});

debouncedWatch(
  minValue,
  () => {
    if (+minValue.value >= +filter.details.limit_max) {
      range.value[0] = +filter.details.limit_max;
    } else if (+minValue.value <= +filter.details.limit_min) {
      range.value[0] = +filter.details.limit_min;
    } else {
      range.value[0] = +minValue.value;
    }
  },
  { debounce: 700 },
);

debouncedWatch(
  maxValue,
  () => {
    if (+maxValue.value >= +filter.details.limit_max) {
      range.value[1] = +filter.details.limit_max;
    } else if (+maxValue.value <= +filter.details.limit_min) {
      range.value[1] = +filter.details.limit_min;
    } else {
      range.value[1] = +maxValue.value;
    }
  },
  { debounce: 700 },
);

const route = useRoute();

async function handleFilterChange() {
  const filterSlug = `filter[${filter.slug}]`;

  if (route.query.page) delete route.query.page;

  await navigateTo({
    path: route.path,
    query: { ...route.query, [filterSlug]: range.value.join(';') },
  });
}

const debouncedFilterChangeHandler = debounce(700, handleFilterChange);
</script>

<template>
  <div class="range-filter">
    <div class="flex justify-between gap-2">
      <InputField
        v-model="minValue"
        :placeholder="minValue"
        class="flex-grow"
        name="from"
        @input="debouncedFilterChangeHandler"
      />

      <InputField
        v-model="maxValue"
        :placeholder="maxValue"
        class="flex-grow"
        name="to"
        @input="debouncedFilterChangeHandler"
      />
    </div>

    <div class="px-2">
      <VueSlider
        v-model="range"
        :dot-options="{ tooltip: 'none', focusStyle: { boxShadow: '.5px .5px 3px 2px #00000042' } }"
        :dot-size="16"
        :max="Number(filter.details.limit_max)"
        :min="Number(filter.details.limit_min)"
        :process-style="{ backgroundColor: '#393d46' }"
        :rail-style="{ backgroundColor: '#d7d8da' }"
        @drag-end="debouncedFilterChangeHandler"
      />
    </div>
  </div>
</template>

<style lang="scss">
.range-filter {
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-size: $text-sm;
}
</style>
